import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { GRANTS } from '@sonifi/wb-user/services/authorization.service';
import { ROUTES_PATH } from './routesConstants';
import AppLayout from '../components/AppLayout';
import AppSupport from '../components/AppSupport';
import { loaderAuthHome, authGuard, loaderAuthProduct } from './guards';
import {
  loaderGrMenu,
  loaderGrMenuId,
} from '@sonifi/wb-guest-request/loaders/loaderGrMenu';
import { loaderOrderRemotes } from '@sonifi/wb-remote-ordering/loaders/loaderOrderRemotes';
import ErrorPage from '../components/ErrorPage/ErrorPage';

// Loaders
import {
  loaderGrOrderById,
  loaderGrOrders,
} from '@sonifi/wb-guest-request/loaders/loaderGrOrders';
import { loaderGrSettings } from '@sonifi/wb-guest-request/loaders/loaderGrSettings';
import { loaderHome } from '@sonifi/wb-home/loaders/loaderHome';
import {
  loaderIrdMenuBuilder,
  loaderIrdMenuBuilderNodeId,
} from '@sonifi/wb-in-room-dining/loaders/loaderIrdMenuBuilder';
import {
  loaderIrdOrderById,
  loaderIrdOrders,
} from '@sonifi/wb-in-room-dining/loaders/loaderIrdOrders';
import { loaderIrdSettings } from '@sonifi/wb-in-room-dining/loaders/laoderIrdSettings';
import { loaderJobs, loaderJobById } from '@sonifi/wb-admin/loaders/loaderJobs';
import {
  loaderProperties,
  loaderPropertyById,
} from '@sonifi/wb-admin/loaders/loaderProperties';
import {
  loaderUsers,
  loaderUserById,
} from '@sonifi/wb-admin/loaders/loaderUsers';
import { loaderHappenings } from '@sonifi/wb-happenings/loaders/loadersHappenings';
import {
  loaderHotelDirectory,
  loaderHotelDirectoryNodeId,
} from '@sonifi/wb-hotel-directory/loaders/loaderHotelDirectory';
import {
  loaderBranding,
  loaderStaycastBranding,
} from '@sonifi/wb-branding/loaders/loaderBranding';
import WbNav from '@sonifi/wb-nav/components/WbNav';
import { NAV_KEYS } from '@sonifi/wb-nav/constants/wbNavConstants';

// Route Pages
const Home = lazy(() => import('@sonifi/wb-home/components/Home'));
const Branding = lazy(() => import('@sonifi/wb-branding/components/Branding'));
const StaycastBranding = lazy(() =>
  import('@sonifi/wb-branding/components/StaycastBranding'),
);
const IrdDataList = lazy(() =>
  import('@sonifi/wb-in-room-dining/DataList/DataList'),
);
const Orders = lazy(() => import('@sonifi/wb-in-room-dining/Orders/Orders'));
const IrdSettings = lazy(() =>
  import('@sonifi/wb-in-room-dining/Settings/Settings'),
);
const IrdMenuBuilder = lazy(() =>
  import('@sonifi/wb-in-room-dining/MenuBuilder/MenuBuilder'),
);
const OrderRemotesForm = lazy(() =>
  import('@sonifi/wb-remote-ordering/components/OrderRemotesForm'),
);
const SuccessCard = lazy(() =>
  import('@sonifi/wb-remote-ordering/components/SuccessCard'),
);
const Jobs = lazy(() => import('@sonifi/wb-admin/jobs/components/Jobs'));
const Users = lazy(() => import('@sonifi/wb-admin/users/components/Users'));
const Happenings = lazy(() =>
  import('@sonifi/wb-happenings/components/Happenings'),
);
const Reporting = lazy(() =>
  import('@sonifi/wb-reporting/components/Reporting'),
);
const TechPubsDocs = lazy(() =>
  import('@sonifi/wb-tech-pubs-docs/components/TechPubsDocs'),
);
const Properties = lazy(() =>
  import('@sonifi/wb-admin/properties/components/Properties'),
);
const HotelDirectory = lazy(() =>
  import('@sonifi/wb-hotel-directory/components/HotelDirectory'),
);

const GrMenu = lazy(() => import('@sonifi/wb-guest-request/Menu/GrMenu'));
const GrSettings = lazy(() =>
  import('@sonifi/wb-guest-request/Settings/GrSettings'),
);
const GrOrders = lazy(() => import('@sonifi/wb-guest-request/Orders/GrOrders'));

const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorPage />,
    HydrateFallback: () => <></>,
    loader: loaderAuthHome(loaderHome),
    element: (
      <AppLayout>
        <WbNav key={NAV_KEYS.home} />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <Home />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/branding/general`,
    middleware: [
      authGuard({
        permissions: [GRANTS.SONIFI_ADMIN],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderBranding),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.branding}
          tertiaryNav={NAV_KEYS.brandingGeneral}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <Branding />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/branding/staycast`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_STAYCAST],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderStaycastBranding),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.branding}
          tertiaryNav={NAV_KEYS.brandingStaycast}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <StaycastBranding />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES_PATH.SELF_SERVICE.ORDER_REMOTES,
    middleware: [
      authGuard({
        permissions: [GRANTS.MANAGE_REMOTE_CONTROL_ORDERS],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderOrderRemotes),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.orderRemotes}
          primaryNav={NAV_KEYS.selfService}
          secondaryNav={NAV_KEYS.orderRemotes}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <OrderRemotesForm />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.SELF_SERVICE.ORDER_REMOTES_SUCCESS}/:orderNum`,
    middleware: [
      authGuard({
        permissions: [GRANTS.MANAGE_REMOTE_CONTROL_ORDERS],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.orderRemotes}
          primaryNav={NAV_KEYS.selfService}
          secondaryNav={NAV_KEYS.orderRemotes}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <SuccessCard />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES_PATH.ADMIN.USERS,
    middleware: [
      authGuard({
        permissions: [GRANTS.CREATE_USERS],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.adminUsers}
          primaryNav={NAV_KEYS.admin}
          secondaryNav={NAV_KEYS.adminUsers}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderUsers),
        element: (
          <Suspense>
            <Users />
          </Suspense>
        ),
      },
      {
        path: ':id',
        loader: loaderAuthProduct(loaderUserById),
        element: (
          <Suspense>
            <Users />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES_PATH.ADMIN.PROPERTIES,
    middleware: [
      authGuard({
        permissions: [GRANTS.SONIFI_ADMIN],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.adminProperties}
          primaryNav={NAV_KEYS.admin}
          secondaryNav={NAV_KEYS.adminProperties}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderProperties),
        element: (
          <Suspense>
            <Properties />
          </Suspense>
        ),
      },
      {
        path: ':id',
        loader: loaderAuthProduct(loaderPropertyById),
        element: (
          <Suspense>
            <Properties />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES_PATH.ADMIN.JOBS,
    middleware: [
      authGuard({
        permissions: [GRANTS.SONIFI_ADMIN],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.adminJobs}
          primaryNav={NAV_KEYS.admin}
          secondaryNav={NAV_KEYS.adminJobs}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderJobs),
        element: (
          <Suspense>
            <Jobs />
          </Suspense>
        ),
      },
      {
        path: ':id',
        loader: loaderAuthProduct(loaderJobById),
        element: (
          <Suspense>
            <Jobs />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_GROUPS],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderHome),
        element: (
          <Suspense>
            <Home />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/happenings/slides?/:slideId?`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_GROUP_CHANNEL, GRANTS.ACCESS_HAPPENINGS],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderHappenings),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.happenings}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <Happenings />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/hotel_directory/:platform`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_HOTEL_DIRECTORY],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.hotelDirectory}
        />
      </AppLayout>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <HotelDirectory />
          </Suspense>
        ),
        children: [
          {
            index: true,
            loader: loaderAuthProduct(loaderHotelDirectory),
          },
          {
            path: ':id',
            loader: loaderAuthProduct(loaderHotelDirectoryNodeId),
            element: <HotelDirectory />,
          },
        ],
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/guest_request/menu/:platform`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_GUEST_REQUEST],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.guestRequest}
          tertiaryNav={NAV_KEYS.guestRequestMenu}
        />
      </AppLayout>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <GrMenu />
          </Suspense>
        ),
        children: [
          {
            index: true,
            loader: loaderAuthProduct(loaderGrMenu),
            element: <Outlet />,
          },
          {
            path: ':id',
            loader: loaderAuthProduct(loaderGrMenuId),
            element: <Outlet />,
          },
        ],
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/guest_request/settings/`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_GUEST_REQUEST],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderGrSettings),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.guestRequest}
          tertiaryNav={NAV_KEYS.guestRequestSettings}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <GrSettings />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/guest_request/orders`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_GUEST_REQUEST],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.guestRequest}
          tertiaryNav={NAV_KEYS.guestRequestOrders}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderGrOrders),
        element: (
          <Suspense>
            <GrOrders />
          </Suspense>
        ),
      },
      {
        path: ':id',
        loader: loaderAuthProduct(loaderGrOrderById),
        element: (
          <Suspense>
            <GrOrders />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/reporting`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_REPORTING],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.reporting}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <Reporting />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/data`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.inRoomDining}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <IrdDataList />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/orders`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.inRoomDining}
          tertiaryNav={NAV_KEYS.inRoomDiningOrders}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        loader: loaderAuthProduct(loaderIrdOrders),
        element: (
          <Suspense>
            <Orders />
          </Suspense>
        ),
      },
      {
        path: ':id',
        loader: loaderAuthProduct(loaderIrdOrderById),
        element: (
          <Suspense>
            <Orders />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/settings`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
      }),
    ],
    HydrateFallback: () => <></>,
    loader: loaderAuthProduct(loaderIrdSettings),
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.inRoomDining}
          tertiaryNav={NAV_KEYS.inRoomDiningSettings}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <IrdSettings />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: `${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/menu/:platform`,
    middleware: [
      authGuard({
        permissions: [GRANTS.ACCESS_IN_ROOM_DINING],
      }),
    ],
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.products}
          primaryNav={NAV_KEYS.products}
          secondaryNav={NAV_KEYS.inRoomDining}
          tertiaryNav={NAV_KEYS.inRoomDiningMenu}
        />
      </AppLayout>
    ),
    children: [
      {
        path: '',
        element: (
          <Suspense>
            <IrdMenuBuilder />
          </Suspense>
        ),
        children: [
          {
            index: true,
            loader: loaderAuthProduct(loaderIrdMenuBuilder),
          },
          {
            path: ':id',
            loader: loaderAuthProduct(loaderIrdMenuBuilderNodeId),
            element: <IrdMenuBuilder />,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES_PATH.HELP_AND_TRAINING.DOCUMENTATION,
    HydrateFallback: () => <></>,
    element: (
      <AppLayout>
        <WbNav
          key={NAV_KEYS.helpAndTraining}
          primaryNav={NAV_KEYS.helpAndTraining}
          secondaryNav={NAV_KEYS.helpAndTraining}
        />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <TechPubsDocs />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: ROUTES_PATH.NO_AUTH,
    element: (
      <AppLayout>
        <WbNav />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: <AppSupport />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
]);

const AppRoutes = () => <RouterProvider router={router} />;

export default AppRoutes;
